import type { CSSProperties, MouseEventHandler } from 'react'
import { useState } from 'react'
import type { AttributeDefinition, GarmentCard } from '@fitting-room/contracts'
import { GarmentFacts } from './GarmentFacts'
import { bestImage } from '../lib/media'
import { uiStrings } from '../lib/strings'
import { text } from '../lib/text'

/**
 * One garment as a card: a photograph in the tenant's own frame, with what the
 * garment says about itself under it.
 *
 * The shortlist and the summary draw this. The deck no longer does — browsing
 * is a feed of photographs now, and the words there are one tap away rather
 * than printed under every gown — so the card has gone back to being a card:
 * a fixed frame with a fixed aspect, which is what the two screens that keep
 * it want.
 *
 * Which attributes appear is decided by `customerVisible` on the tenant's own
 * definitions, in `GarmentFacts`, which the feed's panel shares: the card has
 * no idea which of them is a shape, a fabric or a colour, and never names one.
 */
export function GarmentCardFace({
  card,
  attributes,
  locale,
  compact = false,
  onEnlarge,
}: {
  readonly card: GarmentCard
  readonly attributes: readonly AttributeDefinition[]
  readonly locale: string
  readonly compact?: boolean
  /**
   * When given, and when there is a photograph to enlarge, the picture becomes
   * a control. The handler gets the event so the caller can keep hold of the
   * button it came from — that is what focus has to return to when whatever it
   * opened closes again, and `document.activeElement` is not reliable here
   * because a browser need not focus a button it was merely clicked on.
   */
  readonly onEnlarge?: MouseEventHandler<HTMLButtonElement>
}) {
  const t = uiStrings(locale)
  const { garment } = card
  const photograph = bestImage(garment.media)

  /*
   * A photograph that does not arrive is not a photograph.
   *
   * The media URLs are upstream's, and upstream's expire: Airtable signs its
   * attachment links for a few hours, so a catalogue imported before the
   * boutique's own media store was configured serves a deck of 410s. What that
   * looked like was the browser's broken-image glyph in every frame — a bride
   * being shown, over and over, that something is missing. There is already a
   * fallback for a gown with no photograph at all, and a gown whose photograph
   * cannot be fetched is the same thing as far as the screen is concerned, so
   * it uses that rather than inventing a second, worse one.
   */
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null)
  const image = photograph && photograph.src !== brokenSrc ? photograph : null

  const name = text(garment.name, locale)

  /*
   * The image aspect is the one visual decision that depends on the SHAPE of
   * the thing being sold — a floor-length garment and a pair of glasses do
   * not photograph to the same frame. It is therefore a branding token with
   * a portrait default, not a constant: a tenant selling something wider
   * sets `--fr-card-aspect` and this file does not change.
   */
  const frameStyle: CSSProperties = {
    aspectRatio: compact ? 'var(--fr-card-aspect-compact, var(--fr-card-aspect, 3 / 4))' : 'var(--fr-card-aspect, 4 / 5)',
    background: 'var(--fr-raised)',
  }

  const frame = (
    <>
      {image ? (
        <img
          src={image.src}
          alt={image.alt || name}
          className="h-full w-full object-cover"
          loading="lazy"
          draggable={false}
          onError={() => setBrokenSrc(image.src)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <span className="fr-eyebrow">{name}</span>
        </div>
      )}
    </>
  )

  return (
    <article className="fr-card-face flex h-full w-full flex-col">
      {/* Only a photograph is worth enlarging — the monogram fallback is
          already as big as it will ever get, so it stays a plain frame. */}
      {onEnlarge && image ? (
        <button
          type="button"
          className="fr-card-photo relative block w-full shrink-0 overflow-hidden"
          style={frameStyle}
          aria-label={t.enlargePhoto(name)}
          onClick={onEnlarge}
        >
          {frame}
        </button>
      ) : (
        <div className="relative w-full shrink-0 overflow-hidden" style={frameStyle}>
          {frame}
        </div>
      )}

      {/*
        `shrink-0` on the name and on the footer, `min-h-0` on the attributes:
        the card is a FIXED height, so something has to give when a garment is
        tagged more richly than the one the height was drawn against, and the
        sizes line is the last thing that may. What she loses is the tail of a
        list of details; what she must not lose is whether it is on the floor.
      */}
      <div className={compact ? 'flex min-h-0 flex-1 flex-col gap-2 p-3' : 'flex min-h-0 flex-1 flex-col gap-2 p-4'}>
        <GarmentFacts card={card} attributes={attributes} locale={locale} compact={compact} />
      </div>
    </article>
  )
}
